import {ButtonComponent} from "../../buttons/button/button.component";
import {ButtonConnotation, ButtonProminence} from "../../buttons/button/button";
import {IconButtonComponent} from "../../buttons/icon-button/icon-button.component";
import {IconButtonType} from "../../buttons/icon-button/icon-button";
import {LinkComponent} from "../../buttons/link/link.component";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    Signal,
    signal,
    viewChild,
    WritableSignal
} from "@angular/core";
import {navParam} from "../../navigation/navigation-params";
import {Page} from "../../navigation/page";
import {DataClientError, DisplayableDataServerMessage, EntityData, ValidationMessage} from "@fedelta-pos/data-client";
import {ContentMessageComponent} from "../../notification/message/content-message.component";
import {MessageLevel} from "../../notification/message/message";
import {NotificationService} from "../../notification/notification.service";
import {LoadingService} from "../../ui-elements/loading/loading.service";
import {ToggleComponent} from "../../ui-elements/toggle/toggle.component";
import {TouchableDirective} from "../../ui-elements/touchable.directive";
import {GroupOrderPageMode} from "./group-order-page-mode";
import {FormComponent} from '../../inputs/form/form.component';
import {SelectorType} from '../../inputs/selector-control/selector';
import {SelectorControlComponent} from '../../inputs/selector-control/selector-control.component';
import {TextFieldComponent} from '../../inputs/text-field/text-field.component';
import {GroupOrderModel} from "../group-order.model";
import {OrderModel, TransferRequest} from '../../order/order.model';
import {ReorderStatus} from '../../ordering-page/reorder-status';
import {OrderTransferTargetStatus} from "../order-transfer-request-poll.service";
import {OrderTransferTargetReferenceData} from "../order-transfer-target-entity.model";
import {NgClass} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {ModalPageLayoutComponent} from '../../page-layout/modal-page-layout/modal-page-layout.component';
import {FormsModule} from '@angular/forms';
import {HtmlPipe} from '../../utils/html.pipe';
import {PossessivePipe} from '../../utils/possessive.pipe';
import {CurrencyPipe} from '../../utils/currency.pipe';
import {PageFooterControlDirective} from '../../page-layout/page-footer-control.directive';
import {IdentityData} from '../../identity/identity-entity.model';
import {IdentityModel} from '../../identity/identity.model';
import {ActivatableDirective} from '../../ui-elements/activatable.directive';

@Component({
    selector: "woa-group-order-page",
    templateUrl: "./group-order-page.component.html",
    styleUrl: "./group-order-page.component.scss",
    providers: [{provide: Page, useExisting: GroupOrderPageComponent}],
    imports: [
        ButtonComponent,
        ContentMessageComponent,
        CurrencyPipe,
        FormComponent,
        FormsModule,
        HtmlPipe,
        IconButtonComponent,
        LinkComponent,
        ModalPageLayoutComponent,
        NgClass,
        PageFooterControlDirective,
        PossessivePipe,
        SelectorControlComponent,
        TextFieldComponent,
        ToggleComponent,
        TouchableDirective,
        TranslateModule,
        ActivatableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GroupOrderPageComponent extends Page implements AfterViewInit {
    private loadingService: LoadingService = inject(LoadingService);
    private notificationService: NotificationService = inject(NotificationService);
    public orderModel: OrderModel = inject(OrderModel);
    public groupOrderModel: GroupOrderModel = inject(GroupOrderModel);
    private identityModel: IdentityModel = inject(IdentityModel);

    ButtonProminence = ButtonProminence;
    ButtonConnotation = ButtonConnotation;
    OrderTransferTargetStatus = OrderTransferTargetStatus;
    IconButtonType = IconButtonType;
    GroupOrderPageMode = GroupOrderPageMode;
    MessageLevel = MessageLevel;
    SelectorType = SelectorType;

    public readonly form: Signal<FormComponent> = viewChild.required(FormComponent);

    public readonly mode: WritableSignal<GroupOrderPageMode> = navParam.required<GroupOrderPageMode>("mode");
    public readonly hideNewGroupOrder: Signal<boolean> = navParam("hideNewGroupOrder", false);
    public readonly reorderStatus: Signal<ReorderStatus> = navParam<ReorderStatus>("reorderStatus");

    public readonly newGroupOrderName: WritableSignal<string> = signal<string>(
        this.reorderStatus() ? this.reorderStatus().reorderGroupOrderName
            : (this.groupOrderModel.orderTransferTarget()?.id) || this.groupOrderModel.groupOrderStatus() === OrderTransferTargetStatus.CLOSED_TIMEOUT ? this.groupOrderModel.orderTransferTarget()!.name
                : this.identityModel.currentIdentity() ? this.identityModel.currentIdentity().firstName
                    : undefined);

    public readonly currentGroupOrderName: Signal<string> = signal<string>(this.newGroupOrderName());

    public readonly orderFirstName: WritableSignal<string> = signal<string>(this.identityModel.currentIdentity().firstName);

    public readonly errorKey: WritableSignal<string> = signal(undefined);
    public readonly errorParams: WritableSignal<Record<string, string | number>> = signal(undefined);

    public readonly existingTransferTarget: Signal<OrderTransferTargetReferenceData> = computed(() =>
        this.groupOrderModel.selectedTransferTarget() ? this.orderModel.initiatedOrder().availableTransferTargets
            .find((target: OrderTransferTargetReferenceData) => target.id === this.groupOrderModel.selectedTransferTarget().id) : undefined);

    public readonly otherTransferTargets: Signal<OrderTransferTargetReferenceData[]> = computed(() =>
        this.orderModel.initiatedOrder().availableTransferTargets
            .filter((target: OrderTransferTargetReferenceData) => target !== this.existingTransferTarget()));

    public readonly newestOtherTransferTarget: Signal<OrderTransferTargetReferenceData> = computed(() =>
        this.otherTransferTargets().length > 0 ? this.otherTransferTargets()[0] : undefined);

    public readonly selectedTransferTarget: WritableSignal<OrderTransferTargetReferenceData> = signal<OrderTransferTargetReferenceData>(
        this.newestOtherTransferTarget() ?? this.existingTransferTarget());

    public readonly title: Signal<string> = computed(() =>
        this.mode() === GroupOrderPageMode.PARTICIPANTS ? (this.newestOtherTransferTarget() ? "woa.heading.group-order.title.join" : "woa.heading.group-order.title.joined")
            : this.groupOrderModel.groupOrderStatus() === OrderTransferTargetStatus.OPEN ? "woa.heading.group-order.title.update"
                : !this.groupOrderModel.canStartGroupOrder() ? "woa.heading.group-order.title.rescan"
                    : this.reorderStatus() ? "woa.heading.group-order.title.recreate"
                        : this.groupOrderModel.groupOrderStatus() === OrderTransferTargetStatus.CLOSED ? "woa.heading.group-order.title.create"
                            : "woa.heading.group-order.title.continue");

    public readonly showEditIntroHeading: Signal<boolean> = computed(() =>
        this.groupOrderModel.canStartGroupOrder() && this.groupOrderModel.groupOrderStatus() === OrderTransferTargetStatus.CLOSED);

    public readonly showEditInstructions: Signal<boolean> = computed(() =>
        this.groupOrderModel.groupOrderStatus() === OrderTransferTargetStatus.CLOSED && this.orderModel.totalTransfers() === 0);

    public readonly showEditParticipants: Signal<boolean> = computed(() =>
        this.groupOrderModel.groupOrderStatus() === OrderTransferTargetStatus.OPEN || this.orderModel.totalTransfers() > 0);

    public readonly editIntroBody: Signal<string> = computed(() =>
        !this.groupOrderModel.canStartGroupOrder() ? "woa.message.group-order.intro.rescan"
            : this.groupOrderModel.groupOrderStatus() === OrderTransferTargetStatus.OPEN ? "woa.message.group-order.intro.update"
                : this.groupOrderModel.groupOrderStatus() === OrderTransferTargetStatus.CLOSED ? "woa.message.group-order.intro.create.body"
                    : this.groupOrderModel.groupOrderStatus() === OrderTransferTargetStatus.CLOSED_TIMEOUT ? (this.orderModel.totalTransfers() ? "woa.message.group-order.intro.continueTimeout.withRequests" : "woa.message.group-order.intro.continueTimeout.withoutRequests")
                        : this.groupOrderModel.groupOrderStatus() === OrderTransferTargetStatus.CLOSED_CONFLICT ? (this.orderModel.totalTransfers() ? "woa.message.group-order.intro.continueConflict.withRequests" : "woa.message.group-order.intro.continueConflict.withoutRequests")
                            : undefined);

    public readonly participantIntroHeading: Signal<string> = computed(() =>
        this.newestOtherTransferTarget() ? "woa.message.group-order.intro.join.heading" : "woa.message.group-order.intro.joined.heading");

    public readonly participantIntroBody: Signal<string> = computed(() =>
        this.newestOtherTransferTarget() ? "woa.message.group-order.intro.join.body" : "woa.message.group-order.intro.joined.body");

    public readonly editParticipantsHeading: Signal<string> = computed(() =>
        this.groupOrderModel.groupOrderStatus() === OrderTransferTargetStatus.CLOSED ? "woa.heading.group-order.participants.closed" : "woa.heading.group-order.participants.open");

    public readonly cancelButtonLabel: Signal<string> = computed(() =>
        this.reorderStatus() ? "woa.button.group-order.noRecreate" : "woa.button.group-order.cancel");

    public readonly joinButtonLabel: Signal<string> = computed(() =>
        this.newestOtherTransferTarget() ? "woa.button.group-order.join" : "woa.button.group-order.keep");

    public readonly saveButtonLabel: Signal<string> = computed(() =>
        !this.groupOrderModel.canStartGroupOrder() ? "woa.button.common.ok"
            : this.groupOrderModel.groupOrderStatus() === OrderTransferTargetStatus.OPEN ? "woa.button.group-order.update"
                : this.reorderStatus() ? "woa.button.group-order.recreate"
                    : this.groupOrderModel.groupOrderStatus() === OrderTransferTargetStatus.CLOSED ? "woa.button.group-order.create"
                        : "woa.button.group-order.continue");

    public instructionsExpanded: boolean = false;
    public targetsExpanded: boolean = false;
    public removedRequestIds: Set<string> = new Set<string>();

    constructor() {
        super();

        if (this.newestOtherTransferTarget()) {
            this.groupOrderModel.lastTransferTargetProposed.set(this.newestOtherTransferTarget().created.getTime());
        }
    }

    async ngAfterViewInit(): Promise<void> {
        if (this.reorderStatus()) {
            if (this.reorderStatus().groupOrderValidationMessages) {
                this.form().applyServerValidationMessages(this.reorderStatus().groupOrderValidationMessages);
            } else if (this.reorderStatus().groupOrderError) {
                const groupOrderError: unknown = this.reorderStatus().groupOrderError;
                const displayableMessage: DisplayableDataServerMessage = groupOrderError instanceof DataClientError
                    ? await groupOrderError.displayableMessage
                    : undefined;
                this.errorKey.set(displayableMessage ? displayableMessage.messageKey : "woa.message.group-order.error.recreateFailed");
                this.errorParams.set(displayableMessage ? displayableMessage.messageParams : {});
            }
        }
    }

    override async onWillDismissPage(confirmed: boolean): Promise<boolean> {
        if (this.reorderStatus() && !confirmed) {
            await this.closeGroupOrder();
        }
        return true;
    }

    public toggleParticipantTargets(targetList: HTMLElement): void {
        this.targetsExpanded = !this.targetsExpanded;
        setTimeout(() => {
            targetList.style.maxHeight = this.targetsExpanded ? (targetList.scrollHeight + 2) + "px" : "0";
            if (this.targetsExpanded) {
                setTimeout(() => targetList.scrollIntoView({
                    block: "end",
                    inline: "nearest",
                    behavior: "smooth"
                }), 330);
            }
        });
    }

    public selectParticipantTarget(target: OrderTransferTargetReferenceData): void {
        this.selectedTransferTarget.set(target);
    }

    public toggleRequest(request: TransferRequest): void {
        if (this.removedRequestIds.has(request.transferRequestId)) {
            this.removedRequestIds.delete(request.transferRequestId);
        } else {
            this.removedRequestIds.add(request.transferRequestId);
        }
    }

    public toggleEditInstructions(instructions: HTMLElement): void {
        this.instructionsExpanded = !this.instructionsExpanded;
        setTimeout(() => {
            instructions.style.maxHeight = this.instructionsExpanded ? (instructions.scrollHeight + 1) + "px" : "0";
            if (this.instructionsExpanded) {
                setTimeout(() => instructions.scrollIntoView({
                    block: "end",
                    inline: "nearest",
                    behavior: "smooth"
                }), 330);
            }
        });
    }

    public async saveGroupOrder(): Promise<void> {
        if (!this.groupOrderModel.canStartGroupOrder()) {
            void this.dismiss();
            return;
        }

        this.errorKey.set(undefined);
        if (!this.form().valid) {
            return;
        }

        await this.loadingService.performWithSpinner(async () => {
            let success: boolean = false;
            try {
                const validationMessages: ValidationMessage[] = await this.groupOrderModel.saveGroupOrder(this.groupOrderModel.groupOrderStatus() !== OrderTransferTargetStatus.OPEN, this.newGroupOrderName());
                this.loadingService.dismissSpinner();
                if (validationMessages) {
                    if (this.groupOrderModel.groupOrderStatus() === OrderTransferTargetStatus.OPEN) {
                        this.groupOrderModel.orderTransferTarget().name = this.currentGroupOrderName();
                    }
                    this.form().applyServerValidationMessages(validationMessages);
                } else {
                    success = true;
                }
            } catch (error) {
                if (this.groupOrderModel.groupOrderStatus() === OrderTransferTargetStatus.OPEN) {
                    this.groupOrderModel.orderTransferTarget().name = this.currentGroupOrderName();
                }
                const displayableMessage: DisplayableDataServerMessage = error instanceof DataClientError ? await error.displayableMessage : undefined;
                this.errorKey.set(displayableMessage ? displayableMessage.messageKey : OrderTransferTargetStatus.OPEN ? "woa.message.group-order.error.updateFailed" : "woa.message.group-order.error.createFailed");
                this.errorParams.set(displayableMessage ? displayableMessage.messageParams : {});
            }

            if (success) {
                if (this.removedRequestIds.size) {
                    this.orderModel.removeTransferRequestOrderItems((transferRequestId: string) => this.removedRequestIds.has(transferRequestId));
                }

                await this.dismiss(true);
            }
        }, undefined, this.groupOrderModel.groupOrderStatus() === OrderTransferTargetStatus.OPEN ? "woa.message.group-order.updating" : "woa.message.group-order.creating");
    }

    public async closeGroupOrder(): Promise<void> {
        if (this.groupOrderModel.groupOrderStatus() !== OrderTransferTargetStatus.OPEN && !this.reorderStatus()) {
            void this.dismiss();
        } else if (await this.notificationService.displayConfirmation(this.reorderStatus() ? "woa.heading.group-order.confirmNoRecreate" : "woa.heading.group-order.confirmCancel", undefined,
            this.reorderStatus() ? "woa.message.group-order.confirmNoRecreate" : "woa.message.group-order.confirmCancelGroupOrder",
            "woa.button.common.noThanks",
            this.reorderStatus() ? "woa.button.group-order.discard" : "woa.button.group-order.cancel",
            true,
            {groupOrderName: new PossessivePipe().transform(this.currentGroupOrderName())})) {
            await this.groupOrderModel.closeGroupOrder(OrderTransferTargetStatus.CLOSED, !this.reorderStatus(), true);
            void this.dismiss(true);
        }
    }

    public joinGroupOrder(transferTarget: OrderTransferTargetReferenceData | Event): void {
        if (!this.form().valid) {
            return;
        }

        this.identityModel.currentIdentity().firstName = this.orderFirstName();
        this.identityModel.currentIdentity.update(i => <IdentityData>EntityData.create(IdentityData).assign(i));
        this.groupOrderModel.selectedTransferTarget.set(transferTarget instanceof Event ? this.selectedTransferTarget() : transferTarget);
        void this.dismiss(true);
    }

    public startNewGroupOrder(): void {
        this.groupOrderModel.selectedTransferTarget.set(undefined);
        this.mode.set(GroupOrderPageMode.EDIT);
    }
}
